import { useState, useMemo, useEffect, createContext, useContext } from 'react';
import { ChevronDown, ChevronRight, Info, X } from 'lucide-react';
import Modal from './Modal';

const HighlightContext = createContext({ highlightedNames: new Set(), setHighlightedVar: () => {} });

// Walk trace tree to find which bindings reference which other bindings
function buildDependencyMap(trace) {
  const deps = {}; // bindingName -> Set of names it references
  function collectRefs(node, into) {
    if (!node) return;
    if (node.type === 'ref') into.add(node.name);
    if (node.type === 'call' && getCategory(node.name) === 'other') into.add(node.name);
    if (node.children) node.children.forEach(c => collectRefs(c, into));
  }
  function walk(node) {
    if (!node) return;
    if (node.type === 'binding') {
      const refs = new Set();
      if (node.children) node.children.forEach(c => collectRefs(c, refs));
      deps[node.name] = refs;
    }
    if (node.children) node.children.forEach(c => walk(c));
  }
  walk(trace);
  return deps;
}

// Get all names that depend on the given variable (transitive)
function getDependents(deps, varName) {
  const result = new Set([varName]);
  let changed = true;
  while (changed) {
    changed = false;
    for (const [binding, refs] of Object.entries(deps)) {
      if (!result.has(binding) && [...refs].some(r => result.has(r))) {
        result.add(binding);
        changed = true;
      }
    }
  }
  return result;
}

// Check if a trace node or any descendant references a name in the set
function nodeContainsHighlighted(node, names) {
  if (!node || names.size === 0) return false;
  if (names.has(node.name)) return true;
  if (node.children) return node.children.some(c => nodeContainsHighlighted(c, names));
  return false;
}

const CATEGORY_COLORS = {
  table: { bg: 'bg-[#127373]/20', border: 'border-[#127373]', text: 'text-[#19a3a3]', dot: 'bg-[#127373]' },
  list: { bg: 'bg-blue-400/20', border: 'border-blue-400', text: 'text-blue-400', dot: 'bg-blue-400' },
  text: { bg: 'bg-[#C2A528]/20', border: 'border-[#C2A528]', text: 'text-[#C2A528]', dot: 'bg-[#C2A528]' },
  number: { bg: 'bg-[#2EA043]/20', border: 'border-[#2EA043]', text: 'text-[#2EA043]', dot: 'bg-[#2EA043]' },
  record: { bg: 'bg-pink-400/20', border: 'border-pink-400', text: 'text-pink-400', dot: 'bg-pink-400' },
  datetime: { bg: 'bg-[#B5CEA8]/20', border: 'border-[#B5CEA8]', text: 'text-[#B5CEA8]', dot: 'bg-[#B5CEA8]' },
  func: { bg: 'bg-sky-400/20', border: 'border-sky-400', text: 'text-sky-400', dot: 'bg-sky-400' },
  connector: { bg: 'bg-red-400/20', border: 'border-red-400', text: 'text-red-400', dot: 'bg-red-400' },
  binding: { bg: 'bg-purple-400/20', border: 'border-purple-400', text: 'text-purple-400', dot: 'bg-purple-400' },
  condition: { bg: 'bg-orange-400/20', border: 'border-orange-400', text: 'text-orange-400', dot: 'bg-orange-400' },
  other: { bg: 'bg-[#AFB5BD]/20', border: 'border-[#AFB5BD]', text: 'text-[#AFB5BD]', dot: 'bg-[#AFB5BD]' },
  result: { bg: 'bg-game-primary/20', border: 'border-game-primary', text: 'text-game-primary', dot: 'bg-game-primary' },
};

function getCategory(name) {
  if (!name) return 'other';
  if (name.startsWith('Table.') || name === '#table') return 'table';
  if (name === '#date' || name === '#time' || name === '#datetime' || name === '#datetimezone' || name === '#duration') return 'datetime';
  if (name === '#binary') return 'func';
  if (name.startsWith('#')) return 'func';
  if (name.startsWith('List.')) return 'list';
  if (name.startsWith('Text.') || name.startsWith('Character.')) return 'text';
  if (name.startsWith('Number.') || name.startsWith('Int') || name.startsWith('Decimal.') || name.startsWith('Currency.') || name.startsWith('Percentage.')) return 'number';
  if (name.startsWith('Record.')) return 'record';
  if (name.startsWith('Date.') || name.startsWith('Time.') || name.startsWith('DateTime.') || name.startsWith('DateTimeZone.') || name.startsWith('Duration.')) return 'datetime';
  if (name.startsWith('Web.') || name.startsWith('Sql.') || name.startsWith('File.') || name.startsWith('Folder.') || name.startsWith('Excel.') || name.startsWith('SharePoint.') || name.startsWith('OData.') || name.startsWith('Odbc.') || name.startsWith('OleDb.') || name.startsWith('Oracle.') || name.startsWith('PostgreSQL.') || name.startsWith('MySQL.') || name.startsWith('Teradata.') || name.startsWith('AzureStorage.') || name.startsWith('AmazonRedshift.') || name.startsWith('GoogleBigQuery.') || name.startsWith('Snowflake.') || name.startsWith('Databricks.') || name.startsWith('ActiveDirectory.') || name.startsWith('Exchange.') || name.startsWith('Hadoop.') || name.startsWith('Sybase.') || name.startsWith('DB2.') || name.startsWith('Informix.') || name.startsWith('Sap.') || name.startsWith('Salesforce.')) return 'connector';
  if (name.startsWith('Value.') || name.startsWith('Type.') || name.startsWith('Function.') || name.startsWith('Expression.') || name.startsWith('Error.') || name.startsWith('Binary.') || name.startsWith('Splitter.') || name.startsWith('Combiner.') || name.startsWith('Comparer.') || name.startsWith('Replacer.') || name.startsWith('Lines.') || name.startsWith('Json.') || name.startsWith('Csv.') || name.startsWith('Xml.')) return 'func';
  if (name === 'meta') return 'func';
  if (name === 'if') return 'condition';
  // Any remaining Namespace.Function pattern (but not numeric values like "99.99")
  if (name.includes('.') && /^[A-Z]/.test(name)) return 'func';
  return 'other';
}

function formatDataShape(summary) {
  if (!summary) return null;
  if (summary.type === 'table') {
    return `Table: ${summary.rows} rows \u00d7 ${summary.columns?.length || 0} cols`;
  }
  if (summary.type === 'list') {
    return `List: ${summary.count} items`;
  }
  if (summary.type === 'record') {
    return `Record: ${summary.fields?.length || 0} fields`;
  }
  if (summary.type === 'function') {
    return `fn: ${summary.name || 'anonymous'}`;
  }
  if (summary.type === 'null') return 'null';
  if (summary.value !== undefined) {
    const val = String(summary.value);
    return val.length > 80 ? val.slice(0, 77) + '...' : val;
  }
  return summary.type;
}

function DataChip({ summary, large = false, accent = false }) {
  const [expanded, setExpanded] = useState(false);
  if (!summary) return null;

  const label = formatDataShape(summary);
  const hasPreview = summary.preview && (
    (Array.isArray(summary.preview) && summary.preview.length > 0) ||
    (typeof summary.preview === 'object' && Object.keys(summary.preview).length > 0)
  );

  return (
    <span className="inline-block align-top">
      <button
        onClick={hasPreview ? () => setExpanded(!expanded) : undefined}
        className={`
          inline-flex items-center gap-1 rounded-md font-mono
          bg-[#2a2d31] border
          ${accent ? 'text-game-text border-game-primary' : 'text-game-text-dim border-[#3a3d41]'}
          ${large ? 'px-2.5 py-0.5 text-[13px]' : 'px-2 py-0.5 text-xs'}
          ${hasPreview ? 'cursor-pointer hover:border-game-primary hover:text-game-text' : 'cursor-default'}
          transition-colors
        `}
      >
        {label}
        {hasPreview && (
          expanded
            ? <ChevronDown size={10} />
            : <ChevronRight size={10} />
        )}
      </button>
      {expanded && hasPreview && (
        <div className="mt-1 p-2 bg-[#1a1b1e] border border-[#3a3d41] rounded-lg text-xs font-mono overflow-x-auto" style={{ maxWidth: 'min(600px, calc(100vw - 6rem))' }}>
          <PreviewContent summary={summary} />
        </div>
      )}
    </span>
  );
}

function PreviewContent({ summary }) {
  if (summary.type === 'table' && Array.isArray(summary.preview)) {
    const cols = summary.columns || [];
    return (
      <table className="text-[11px] min-w-max">
        <thead>
          <tr>
            {cols.map(c => (
              <th key={c} className="px-2 py-1 text-left text-game-text-dim border-b border-[#3a3d41] font-semibold">{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {summary.preview.map((row, i) => (
            <tr key={i}>
              {cols.map(c => (
                <td key={c} className="px-2 py-1 text-game-text">{row[c] ?? ''}</td>
              ))}
            </tr>
          ))}
          {summary.rows > summary.preview.length && (
            <tr>
              <td colSpan={cols.length} className="px-2 py-1 text-game-text-dim italic">
                ...and {summary.rows - summary.preview.length} more rows
              </td>
            </tr>
          )}
        </tbody>
      </table>
    );
  }
  if (summary.type === 'list' && Array.isArray(summary.preview)) {
    return (
      <div className="space-y-0.5">
        {summary.preview.map((item, i) => (
          <div key={i} className="text-game-text">{String(item)}</div>
        ))}
        {summary.count > summary.preview.length && (
          <div className="text-game-text-dim italic">...and {summary.count - summary.preview.length} more</div>
        )}
      </div>
    );
  }
  if (summary.type === 'record' && summary.preview) {
    return (
      <div className="space-y-0.5">
        {Object.entries(summary.preview).map(([k, v]) => (
          <div key={k} className="text-game-text">
            <span className="text-game-text-dim">{k}:</span> {String(v)}
          </div>
        ))}
      </div>
    );
  }
  return null;
}

// Group consecutive children with the same name+type into runs
function groupConsecutiveChildren(children) {
  if (!children || children.length === 0) return [];
  const groups = [];
  let current = { key: `${children[0].type}:${children[0].name}`, items: [children[0]] };

  for (let i = 1; i < children.length; i++) {
    const child = children[i];
    const key = `${child.type}:${child.name}`;
    if (key === current.key) {
      current.items.push(child);
    } else {
      groups.push(current);
      current = { key, items: [child] };
    }
  }
  groups.push(current);
  return groups;
}

function CollapsedGroup({ group }) {
  const [expanded, setExpanded] = useState(false);
  const { highlightedNames } = useContext(HighlightContext);
  const items = group.items;

  // Groups of 1-2 items: show all, no collapsing
  if (items.length <= 2) {
    return items.map((child, i) => {
      const itemHighlighted = nodeContainsHighlighted(child, highlightedNames);
      return (
        <div key={i} className={`py-0.5 transition-colors ${itemHighlighted ? 'exec-highlight' : ''}`}>
          <TraceNodeRenderer node={child} />
        </div>
      );
    });
  }

  const hiddenCount = items.length - 2;

  if (expanded) {
    return (
      <>
        {items.map((child, i) => {
          const itemHighlighted = nodeContainsHighlighted(child, highlightedNames);
          return (
            <div key={i} className={`py-0.5 flex items-start gap-1.5 transition-colors ${itemHighlighted ? 'exec-highlight' : ''}`}>
              <span className="text-[10px] text-game-text-dim font-mono leading-6 w-8 text-right flex-shrink-0">
                {i === 0 ? 'First:' : i === items.length - 1 ? 'Last:' : `#${i + 1}:`}
              </span>
              <TraceNodeRenderer node={child} />
            </div>
          );
        })}
        <div className="py-0.5 ml-[38px]">
          <button
            onClick={() => setExpanded(false)}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-mono
              text-game-text-dim hover:text-game-text hover:bg-[#2a2d31] transition-colors cursor-pointer"
          >
            <ChevronDown size={10} />
            collapse {hiddenCount} iterations
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <div className={`py-0.5 flex items-start gap-1.5 transition-colors ${nodeContainsHighlighted(items[0], highlightedNames) ? 'exec-highlight' : ''}`}>
        <span className="text-[10px] text-game-text-dim font-mono leading-6 w-8 text-right flex-shrink-0">First:</span>
        <TraceNodeRenderer node={items[0]} />
      </div>
      <div className="py-0.5 ml-[38px]">
        <button
          onClick={() => setExpanded(true)}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-mono
            border border-dashed border-[#3a3d41] text-game-text-dim
            hover:text-game-text hover:border-game-primary/50 hover:bg-[#2a2d31]/50
            transition-colors cursor-pointer"
        >
          <ChevronRight size={10} />
          <span>...{hiddenCount} more iteration{hiddenCount > 1 ? 's' : ''}...</span>
        </button>
      </div>
      <div className={`py-0.5 flex items-start gap-1.5 transition-colors ${nodeContainsHighlighted(items[items.length - 1], highlightedNames) ? 'exec-highlight' : ''}`}>
        <span className="text-[10px] text-game-text-dim font-mono leading-6 w-8 text-right flex-shrink-0">Last:</span>
        <TraceNodeRenderer node={items[items.length - 1]} />
      </div>
    </>
  );
}

function CallNode({ node, resultArrow = false }) {
  const { highlightedNames, setHighlightedVar } = useContext(HighlightContext);
  const isRef = node.type === 'ref';
  const rawCategory = getCategory(node.name);
  const category = isRef || (node.type === 'call' && rawCategory === 'other') ? 'binding' : rawCategory;
  const colors = CATEGORY_COLORS[category];
  const allChildren = node.children || [];
  const groups = groupConsecutiveChildren(allChildren);
  const isHighlighted = highlightedNames.has(node.name);
  const isClickable = isRef || (node.type === 'call' && category === 'binding') || isHighlighted;

  return (
    <div className="inline-flex items-start gap-0 flex-wrap">
      <div
        className={`
          inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border
          ${colors.bg} ${colors.border}
          ${isClickable ? 'cursor-pointer hover:brightness-125' : ''}
          ${isHighlighted ? 'ring-2 ring-purple-400/60' : ''}
          transition-all
        `}
        onClick={isClickable ? () => setHighlightedVar(isHighlighted ? null : node.name) : undefined}
      >
        <span className={`w-1.5 h-1.5 rounded-full ${colors.dot} flex-shrink-0`} />
        <span className={`text-xs font-semibold ${colors.text} whitespace-nowrap`}>{node.name}</span>
      </div>

      {/* Output inline when no inner steps */}
      {node.output && allChildren.length === 0 && (
        <>
          <span className={`mx-1 leading-6 ${resultArrow ? 'text-game-primary text-xl font-bold' : 'text-game-text-dim text-base'}`}>{'\u2192'}</span>
          <DataChip summary={node.output} large={resultArrow} accent={resultArrow} />
        </>
      )}

      {allChildren.length > 0 && (
        <div className="ml-4 mt-1 pl-3 border-l border-[#3a3d41]">
          {groups.map((group, gi) => (
            <CollapsedGroup key={gi} group={group} />
          ))}
          {/* Output below inner steps */}
          {node.output && (
            <div className="flex items-start gap-2 pt-1.5 mt-1.5 border-t border-[#3a3d41]/50">
              <span className="text-xs font-bold text-game-primary whitespace-nowrap leading-6">Result:</span>
              <span className="text-game-primary text-base leading-6">{'\u2192'}</span>
              <DataChip summary={node.output} accent />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TraceNodeRenderer({ node, resultArrow = false }) {
  if (node.type === 'call') {
    return <CallNode node={node} resultArrow={resultArrow} />;
  }
  if (node.type === 'ref') {
    return <CallNode node={node} resultArrow={resultArrow} />;
  }
  if (node.type === 'let') {
    return <ExecutionPlanTree trace={node} />;
  }
  if (node.type === 'binding') {
    return <BindingNode node={node} isLast index={0} total={1} />;
  }
  // Fallback for unknown types
  return (
    <div className="text-xs text-game-text-dim">
      {node.name || node.type}
      {node.output && (
        <>
          <span className="text-game-text-dim text-base mx-1 leading-6">{'\u2192'}</span>
          <DataChip summary={node.output} />
        </>
      )}
    </div>
  );
}

function BindingNode({ node, isLast, index }) {
  const { highlightedNames, setHighlightedVar } = useContext(HighlightContext);
  const colors = CATEGORY_COLORS.binding;
  const allChildren = node.children || [];
  const isHighlighted = highlightedNames.has(node.name);

  return (
    <div className={`relative transition-colors ${isHighlighted ? 'exec-highlight' : ''}`}>
      <div className="flex items-stretch">
        {/* Connector column */}
        <div className="flex flex-col items-center w-5 flex-shrink-0">
          <div className={`w-px ${index === 0 ? 'bg-transparent' : 'bg-[#3a3d41]'}`} style={{ height: 14 }} />
          <div className={`w-2 h-2 rounded-full ${colors.dot} flex-shrink-0`} />
          <div className={`w-px flex-1 ${isLast ? 'bg-transparent' : 'bg-[#3a3d41]'}`} />
        </div>

        {/* Content */}
        <div className="py-1.5 pl-1 min-w-0 flex-1">
          {/* Top row: name + inline call chain */}
          {(() => {
            const callCount = allChildren.filter(c => c.type === 'call').length;
            const useVertical = callCount > 1;
            return (
              <>
                <div className="flex items-start gap-2 flex-wrap">
                  <span
                    className={`text-sm font-semibold ${colors.text} whitespace-nowrap leading-6 cursor-pointer hover:underline`}
                    onClick={() => setHighlightedVar(highlightedNames.has(node.name) && highlightedNames.size > 0 ? null : node.name)}
                  >
                    {node.name}
                  </span>

                  {allChildren.length > 0 && (
                    <span className="text-game-text-dim text-base leading-6">{'\u2550'}</span>
                  )}

                  {/* Render children inline when few */}
                  {!useVertical && allChildren.map((child, i) => (
                    <TraceNodeRenderer key={i} node={child} />
                  ))}
                </div>
                {/* Render children vertically when many calls */}
                {useVertical && (
                  <div className="ml-4 mt-1 pl-3 border-l border-[#3a3d41]">
                    {allChildren.map((child, i) => (
                      <div key={i} className="py-0.5">
                        <TraceNodeRenderer node={child} />
                      </div>
                    ))}
                  </div>
                )}
                {/* If no children, just show the output */}
                {allChildren.length === 0 && node.output && (
                  <div className="flex items-start gap-2">
                    <span className="text-game-text-dim text-base leading-6">{'\u2192'}</span>
                    <DataChip summary={node.output} />
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </div>
    </div>
  );
}

function ExecutionPlanTree({ trace }) {
  const { highlightedNames } = useContext(HighlightContext);
  if (!trace) return null;

  if (trace.type === 'root' || trace.type === 'let') {
    const bindingChildren = (trace.children || []).filter(c => c.type === 'binding');
    const otherChildren = (trace.children || []).filter(c => c.type !== 'binding');

    if (bindingChildren.length > 0) {
      return (
        <div>
          {trace.type === 'let' && (
            <div className="text-sm text-game-text-dim font-mono mb-1 flex items-center gap-1">
              <span className="text-purple-400 font-semibold">let...in</span>
            </div>
          )}
          <div>
            {bindingChildren.map((child, i) => (
              <BindingNode
                key={i}
                node={child}
                isLast={!trace.output && i === bindingChildren.length - 1}
                index={i}
                total={bindingChildren.length}
              />
            ))}
          </div>
          {trace.output && (
            <div className="relative transition-colors">
              <div className="flex items-stretch">
                {/* Connector column — same as BindingNode */}
                <div className="flex flex-col items-center w-5 flex-shrink-0">
                  <div className="w-px bg-[#3a3d41]" style={{ height: 14 }} />
                  <div className="w-2 h-2 rounded-full bg-game-primary flex-shrink-0" />
                  <div className="w-px flex-1 bg-transparent" />
                </div>
                {/* Content */}
                {(() => {
                  // Check if result is a single function chain whose output IS the final result
                  const hasRefs = otherChildren.some(c => c.type === 'ref');
                  const lastChild = otherChildren[otherChildren.length - 1];
                  const lastOutputMatches = lastChild?.output && trace.output &&
                    JSON.stringify(lastChild.output) === JSON.stringify(trace.output);
                  const isPureFunc = otherChildren.length > 0 && !hasRefs && lastOutputMatches;
                  const resultCallCount = otherChildren.filter(c => c.type === 'call').length;
                  const useVertical = resultCallCount > 1;
                  return (
                    <div className="py-1.5 pl-1 min-w-0 flex-1">
                      <div className="flex items-start gap-2 flex-wrap">
                        <span className="text-sm font-bold text-game-primary whitespace-nowrap leading-6">
                          Result
                        </span>
                        {otherChildren.length > 0 && (
                          <>
                            <span className="text-game-text-dim text-base leading-6">{'\u2550'}</span>
                            {!useVertical && otherChildren.map((child, i) => (
                              <TraceNodeRenderer
                                key={i}
                                node={child}
                                resultArrow={isPureFunc && i === otherChildren.length - 1}
                              />
                            ))}
                          </>
                        )}
                      </div>
                      {useVertical && (
                        <div className="ml-4 mt-1 pl-3 border-l border-[#3a3d41]">
                          {otherChildren.map((child, i) => {
                            const itemHighlighted = nodeContainsHighlighted(child, highlightedNames);
                            return (
                              <div key={i} className={`py-0.5 transition-colors ${itemHighlighted ? 'exec-highlight' : ''}`}>
                                <TraceNodeRenderer
                                  node={child}
                                  resultArrow={isPureFunc && i === otherChildren.length - 1}
                                />
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {!isPureFunc && (
                        <div className="flex items-center gap-2 mt-1.5 ml-0.5">
                          <span className="text-game-primary text-xl font-bold">{'\u2192'}</span>
                          <DataChip summary={trace.output} large accent />
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
          )}
          {trace.not_executed && trace.not_executed.length > 0 && (
            <div className="mt-3 pt-3 border-t border-[#2a2d31]">
              <span className="text-xs text-game-text-dim">Not executed variables: </span>
              {trace.not_executed.map((name, i) => (
                <span key={name}>
                  {i > 0 && <span className="text-game-text-dim text-xs">, </span>}
                  <span className="text-xs text-game-text-dim/60 italic">{name}</span>
                </span>
              ))}
            </div>
          )}
        </div>
      );
    }

    // No bindings - just render all children
    if (trace.children && trace.children.length > 0) {
      return (
        <div className="space-y-1">
          {trace.children.map((child, i) => (
            <TraceNodeRenderer key={i} node={child} />
          ))}
        </div>
      );
    }

    // Simple expression with no function calls or let bindings — just show the output
    if (trace.output) {
      return (
        <div className="flex items-start gap-2">
          <span className="text-sm text-game-primary font-mono font-semibold leading-6">Output</span>
          <span className="text-game-primary text-xl font-bold leading-6">{'\u2192'}</span>
          <DataChip summary={trace.output} large />
        </div>
      );
    }

    return (
      <div className="text-game-text-dim text-sm">No execution steps traced.</div>
    );
  }

  if (trace.type === 'call') {
    return <CallNode node={trace} />;
  }

  return <TraceNodeRenderer node={trace} />;
}

const LEGEND_ITEMS = [
  { category: 'table', label: 'Table operations', example: 'Table.*' },
  { category: 'list', label: 'List operations', example: 'List.*' },
  { category: 'text', label: 'Text operations', example: 'Text.*' },
  { category: 'number', label: 'Number operations', example: 'Number.*' },
  { category: 'record', label: 'Record operations', example: 'Record.*' },
  { category: 'datetime', label: 'Date & Time', example: '#date, Date.*, Duration.*' },
  { category: 'func', label: 'Functions & utilities', example: 'Value.*, Type.*, Json.*' },
  { category: 'connector', label: 'Data connectors', example: 'Web.*, Sql.*, File.*' },
  { category: 'binding', label: 'Variable bindings', example: 'let x = ...' },
  { category: 'condition', label: 'Conditions', example: 'if ... then ... else' },
  { category: 'other', label: 'Other', example: 'literals' },
  { category: 'result', label: 'Result', example: 'final output value' },
];

function ColorLegend() {
  return (
    <div className="grid grid-cols-2 gap-x-6 gap-y-1.5">
      {LEGEND_ITEMS.map(({ category, label, example }) => {
        const colors = CATEGORY_COLORS[category];
        return (
          <div key={category} className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${colors.dot} flex-shrink-0`} />
            <span className={`text-xs font-semibold ${colors.text}`}>{label}</span>
            <span className="text-[10px] text-game-text-dim">{example}</span>
          </div>
        );
      })}
    </div>
  );
}

export default function ExecutionPlanModal({ isOpen, onClose, trace, isLoading }) {
  const [showLegend, setShowLegend] = useState(false);
  const [highlightedVar, setHighlightedVar] = useState(null);

  useEffect(() => {
    if (isOpen) setHighlightedVar(null);
  }, [isOpen]);

  const deps = useMemo(() => trace ? buildDependencyMap(trace) : {}, [trace]);
  const highlightedNames = useMemo(
    () => highlightedVar ? getDependents(deps, highlightedVar) : new Set(),
    [highlightedVar, deps]
  );

  const headerButtons = (
    <div className="flex items-center gap-2">
      {highlightedVar && (
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-game-text-dim">Highlighted:</span>
          <button
            onClick={() => setHighlightedVar(null)}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium
              bg-purple-400/20 text-purple-400 transition-colors hover:bg-purple-400/30"
            title="Clear highlight"
          >
            <span>{highlightedVar}</span>
            <X size={12} />
          </button>
        </div>
      )}
      <button
        onClick={() => setShowLegend(!showLegend)}
        className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium
          transition-colors ${showLegend
            ? 'bg-game-primary/20 text-game-primary'
            : 'text-game-text-dim hover:text-game-text hover:bg-[#2a2d31]'
          }`}
        title="Color legend"
      >
        <Info size={14} />
        <span>Legend</span>
      </button>
    </div>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Execution Plan" size="full-height" headerExtra={headerButtons}>
      {showLegend && (
        <div className="mb-4 p-4 bg-[#1a1b1e] border border-[#2a2d31] rounded-xl">
          <ColorLegend />
        </div>
      )}

      <HighlightContext.Provider value={{ highlightedNames, setHighlightedVar }}>
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="flex items-center gap-3">
              <div className="w-5 h-5 border-2 border-game-primary border-t-transparent rounded-full animate-spin" />
              <span className="text-game-text-dim">Analyzing execution...</span>
            </div>
          </div>
        ) : trace ? (
          <div className="overflow-hidden pr-1">
            <div className="exec-plan-root">
              <ExecutionPlanTree trace={trace} />
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-game-text-dim">
            No trace data available. Run your code with the execution plan button.
          </div>
        )}
      </HighlightContext.Provider>
    </Modal>
  );
}
